import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Switch } from '../ui/switch';
import { useToast } from '../../hooks/use-toast';
import api from '../../lib/api';
import {
  Globe,
  Languages,
  DollarSign,
  Settings,
  RefreshCw,
  Plus,
  Edit,
  Check,
  X,
  ArrowLeftRight
} from 'lucide-react';

export default function LocalizationPage() {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [dashboardData, setDashboardData] = useState(null);
  const [languages, setLanguages] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [exchangeRates, setExchangeRates] = useState([]);
  const [regionalSettings, setRegionalSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showLanguageDialog, setShowLanguageDialog] = useState(false);
  const [showCurrencyDialog, setShowCurrencyDialog] = useState(false);
  const [showRateDialog, setShowRateDialog] = useState(false);
  const [newLanguage, setNewLanguage] = useState({ language_code: '', language_name: '', native_name: '', is_rtl: false, is_default: false });
  const [newCurrency, setNewCurrency] = useState({ currency_code: '', currency_name: '', currency_symbol: '', decimal_places: 2, is_default: false });
  const [newRate, setNewRate] = useState({ from_currency: '', to_currency: '', rate: '', effective_date: '' });
  const { toast } = useToast();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [dashRes, langRes, currRes, ratesRes, settingsRes] = await Promise.all([
        api.get('/api/localization/dashboard'),
        api.get('/api/localization/languages'),
        api.get('/api/localization/currencies'),
        api.get('/api/localization/exchange-rates'),
        api.get('/api/localization/regional-settings')
      ]);
      setDashboardData(dashRes.data);
      setLanguages(langRes.data || []);
      setCurrencies(currRes.data || []);
      setExchangeRates(ratesRes.data || []);
      setRegionalSettings(settingsRes.data);
    } catch (error) {
      console.error('Error fetching localization data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddLanguage = async () => {
    try {
      await api.post('/api/localization/languages', newLanguage);
      toast({ title: 'Language added successfully' });
      setShowLanguageDialog(false);
      setNewLanguage({ language_code: '', language_name: '', native_name: '', is_rtl: false, is_default: false });
      fetchData();
    } catch (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  const handleAddCurrency = async () => {
    try {
      await api.post('/api/localization/currencies', newCurrency);
      toast({ title: 'Currency added successfully' });
      setShowCurrencyDialog(false);
      setNewCurrency({ currency_code: '', currency_name: '', currency_symbol: '', decimal_places: 2, is_default: false });
      fetchData();
    } catch (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  const handleAddRate = async () => {
    try {
      await api.post('/api/localization/exchange-rates', {
        ...newRate,
        rate: parseFloat(newRate.rate)
      });
      toast({ title: 'Exchange rate added successfully' });
      setShowRateDialog(false);
      setNewRate({ from_currency: '', to_currency: '', rate: '', effective_date: '' });
      fetchData();
    } catch (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  const handleUpdateSettings = async (field, value) => {
    try {
      await api.put('/api/localization/regional-settings', { [field]: value });
      toast({ title: 'Settings updated' });
      fetchData();
    } catch (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="localization-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Localization</h1>
          <p className="text-muted-foreground">Manage languages, currencies, and regional settings</p>
        </div>
        <Button onClick={fetchData} variant="outline" size="sm">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="dashboard" data-testid="tab-dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="languages" data-testid="tab-languages">Languages</TabsTrigger>
          <TabsTrigger value="currencies" data-testid="tab-currencies">Currencies</TabsTrigger>
          <TabsTrigger value="exchange-rates" data-testid="tab-exchange-rates">Exchange Rates</TabsTrigger>
          <TabsTrigger value="settings" data-testid="tab-settings">Regional Settings</TabsTrigger>
        </TabsList>

        <TabsContent value="dashboard" className="space-y-6">
          {/* Summary Cards */}
          <div className="grid gap-4 md:grid-cols-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Languages</CardTitle>
                <Languages className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{dashboardData?.languages?.active || 0}</div>
                <p className="text-xs text-muted-foreground">Active languages</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Currencies</CardTitle>
                <DollarSign className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{dashboardData?.currencies?.active || 0}</div>
                <p className="text-xs text-muted-foreground">Supported currencies</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Translation Keys</CardTitle>
                <Globe className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{dashboardData?.translations?.keys || 0}</div>
                <p className="text-xs text-muted-foreground">Defined keys</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Translations</CardTitle>
                <Check className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{dashboardData?.translations?.translated || 0}</div>
                <p className="text-xs text-muted-foreground">Completed translations</p>
              </CardContent>
            </Card>
          </div>

          {/* Quick Info */}
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Active Languages</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {dashboardData?.languages?.items?.map((lang) => (
                    <Badge key={lang.id} variant={lang.is_default ? 'default' : 'outline'}>
                      {lang.language_name} ({lang.language_code})
                      {lang.is_default && ' - Default'}
                    </Badge>
                  ))}
                  {(!dashboardData?.languages?.items || dashboardData.languages.items.length === 0) && (
                    <p className="text-muted-foreground">No languages configured</p>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Active Currencies</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {dashboardData?.currencies?.items?.map((curr) => (
                    <Badge key={curr.id} variant={curr.is_default ? 'default' : 'outline'}>
                      {curr.currency_symbol} {curr.currency_code}
                      {curr.is_default && ' - Default'}
                    </Badge>
                  ))}
                  {(!dashboardData?.currencies?.items || dashboardData.currencies.items.length === 0) && (
                    <p className="text-muted-foreground">No currencies configured</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Current Settings */}
          <Card>
            <CardHeader>
              <CardTitle>Current Regional Settings</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-3">
                <div>
                  <Label className="text-muted-foreground">Default Language</Label>
                  <p className="font-medium">{dashboardData?.regional_settings?.default_language || 'en'}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Default Currency</Label>
                  <p className="font-medium">{dashboardData?.regional_settings?.default_currency || 'ETB'}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Timezone</Label>
                  <p className="font-medium">{dashboardData?.regional_settings?.timezone || 'Africa/Addis_Ababa'}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Date Format</Label>
                  <p className="font-medium">{dashboardData?.regional_settings?.date_format || 'DD/MM/YYYY'}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Time Format</Label>
                  <p className="font-medium">{dashboardData?.regional_settings?.time_format || 'HH:mm'}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Fiscal Year Start</Label>
                  <p className="font-medium">Month {dashboardData?.regional_settings?.fiscal_year_start || 7}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="languages" className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold">Supported Languages</h2>
            <Button onClick={() => setShowLanguageDialog(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add Language
            </Button>
          </div>

          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Code</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Native Name</TableHead>
                  <TableHead>RTL</TableHead>
                  <TableHead>Default</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {languages.map((lang) => (
                  <TableRow key={lang.id}>
                    <TableCell className="font-mono">{lang.language_code}</TableCell>
                    <TableCell>{lang.language_name}</TableCell>
                    <TableCell>{lang.native_name || '-'}</TableCell>
                    <TableCell>{lang.is_rtl ? <Check className="h-4 w-4 text-green-500" /> : <X className="h-4 w-4 text-muted-foreground" />}</TableCell>
                    <TableCell>{lang.is_default ? <Badge>Default</Badge> : '-'}</TableCell>
                    <TableCell>
                      <Badge variant={lang.is_active ? 'default' : 'secondary'}>
                        {lang.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
                {languages.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                      No languages configured. Add your first language.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="currencies" className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold">Supported Currencies</h2>
            <Button onClick={() => setShowCurrencyDialog(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add Currency
            </Button>
          </div>

          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Code</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Symbol</TableHead>
                  <TableHead>Decimals</TableHead>
                  <TableHead>Default</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {currencies.map((curr) => (
                  <TableRow key={curr.id}>
                    <TableCell className="font-mono">{curr.currency_code}</TableCell>
                    <TableCell>{curr.currency_name}</TableCell>
                    <TableCell className="font-mono">{curr.currency_symbol}</TableCell>
                    <TableCell>{curr.decimal_places}</TableCell>
                    <TableCell>{curr.is_default ? <Badge>Default</Badge> : '-'}</TableCell>
                    <TableCell>
                      <Badge variant={curr.is_active ? 'default' : 'secondary'}>
                        {curr.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
                {currencies.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                      No currencies configured. Add your first currency.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="exchange-rates" className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold">Exchange Rates</h2>
            <Button onClick={() => setShowRateDialog(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add Rate
            </Button>
          </div>

          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>From</TableHead>
                  <TableHead></TableHead>
                  <TableHead>To</TableHead>
                  <TableHead>Rate</TableHead>
                  <TableHead>Effective Date</TableHead>
                  <TableHead>Source</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {exchangeRates.map((rate) => (
                  <TableRow key={rate.id}>
                    <TableCell className="font-mono">{rate.from_currency}</TableCell>
                    <TableCell><ArrowLeftRight className="h-4 w-4" /></TableCell>
                    <TableCell className="font-mono">{rate.to_currency}</TableCell>
                    <TableCell className="font-mono">{parseFloat(rate.rate).toFixed(4)}</TableCell>
                    <TableCell>{new Date(rate.effective_date).toLocaleDateString()}</TableCell>
                    <TableCell className="capitalize">{rate.source || 'manual'}</TableCell>
                  </TableRow>
                ))}
                {exchangeRates.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                      No exchange rates configured.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="settings" className="space-y-4">
          <h2 className="text-xl font-semibold">Regional Settings</h2>

          <Card>
            <CardHeader>
              <CardTitle>Property Settings</CardTitle>
              <CardDescription>Configure regional preferences for this property</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>Default Language</Label>
                  <Select
                    value={regionalSettings?.default_language || 'en'}
                    onValueChange={(value) => handleUpdateSettings('default_language', value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {languages.map((lang) => (
                        <SelectItem key={lang.language_code} value={lang.language_code}>
                          {lang.language_name}
                        </SelectItem>
                      ))}
                      <SelectItem value="en">English</SelectItem>
                      <SelectItem value="am">Amharic</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Default Currency</Label>
                  <Select
                    value={regionalSettings?.default_currency || 'ETB'}
                    onValueChange={(value) => handleUpdateSettings('default_currency', value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {currencies.map((curr) => (
                        <SelectItem key={curr.currency_code} value={curr.currency_code}>
                          {curr.currency_symbol} {curr.currency_name}
                        </SelectItem>
                      ))}
                      <SelectItem value="ETB">Ethiopian Birr</SelectItem>
                      <SelectItem value="USD">US Dollar</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Timezone</Label>
                  <Select
                    value={regionalSettings?.timezone || 'Africa/Addis_Ababa'}
                    onValueChange={(value) => handleUpdateSettings('timezone', value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Africa/Addis_Ababa">Africa/Addis_Ababa (UTC+3)</SelectItem>
                      <SelectItem value="UTC">UTC</SelectItem>
                      <SelectItem value="Europe/London">Europe/London</SelectItem>
                      <SelectItem value="America/New_York">America/New_York</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Date Format</Label>
                  <Select
                    value={regionalSettings?.date_format || 'DD/MM/YYYY'}
                    onValueChange={(value) => handleUpdateSettings('date_format', value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="DD/MM/YYYY">DD/MM/YYYY</SelectItem>
                      <SelectItem value="MM/DD/YYYY">MM/DD/YYYY</SelectItem>
                      <SelectItem value="YYYY-MM-DD">YYYY-MM-DD</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Time Format</Label>
                  <Select
                    value={regionalSettings?.time_format || 'HH:mm'}
                    onValueChange={(value) => handleUpdateSettings('time_format', value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="HH:mm">24-hour (14:30)</SelectItem>
                      <SelectItem value="hh:mm A">12-hour (2:30 PM)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Fiscal Year Start Month</Label>
                  <Select
                    value={String(regionalSettings?.fiscal_year_start || 7)}
                    onValueChange={(value) => handleUpdateSettings('fiscal_year_start', parseInt(value))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">January</SelectItem>
                      <SelectItem value="4">April</SelectItem>
                      <SelectItem value="7">July (Ethiopian)</SelectItem>
                      <SelectItem value="10">October</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add Language Dialog */}
      <Dialog open={showLanguageDialog} onOpenChange={setShowLanguageDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Language</DialogTitle>
            <DialogDescription>Add a new supported language</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Language Code</Label>
                <Input
                  placeholder="en, am, fr..."
                  value={newLanguage.language_code}
                  onChange={(e) => setNewLanguage({ ...newLanguage, language_code: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Language Name</Label>
                <Input
                  placeholder="English"
                  value={newLanguage.language_name}
                  onChange={(e) => setNewLanguage({ ...newLanguage, language_name: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Native Name</Label>
              <Input
                placeholder="አማርኛ"
                value={newLanguage.native_name}
                onChange={(e) => setNewLanguage({ ...newLanguage, native_name: e.target.value })}
              />
            </div>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <Switch
                  checked={newLanguage.is_rtl}
                  onCheckedChange={(checked) => setNewLanguage({ ...newLanguage, is_rtl: checked })}
                />
                <Label>Right-to-Left</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Switch
                  checked={newLanguage.is_default}
                  onCheckedChange={(checked) => setNewLanguage({ ...newLanguage, is_default: checked })}
                />
                <Label>Set as Default</Label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLanguageDialog(false)}>Cancel</Button>
            <Button onClick={handleAddLanguage}>Add Language</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Currency Dialog */}
      <Dialog open={showCurrencyDialog} onOpenChange={setShowCurrencyDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Currency</DialogTitle>
            <DialogDescription>Add a new supported currency</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Currency Code</Label>
                <Input
                  placeholder="ETB, USD..."
                  value={newCurrency.currency_code}
                  onChange={(e) => setNewCurrency({ ...newCurrency, currency_code: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Currency Name</Label>
                <Input
                  placeholder="Ethiopian Birr"
                  value={newCurrency.currency_name}
                  onChange={(e) => setNewCurrency({ ...newCurrency, currency_name: e.target.value })}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Symbol</Label>
                <Input
                  placeholder="ብር, $..."
                  value={newCurrency.currency_symbol}
                  onChange={(e) => setNewCurrency({ ...newCurrency, currency_symbol: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Decimal Places</Label>
                <Input
                  type="number"
                  value={newCurrency.decimal_places}
                  onChange={(e) => setNewCurrency({ ...newCurrency, decimal_places: parseInt(e.target.value) })}
                />
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Switch
                checked={newCurrency.is_default}
                onCheckedChange={(checked) => setNewCurrency({ ...newCurrency, is_default: checked })}
              />
              <Label>Set as Default</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCurrencyDialog(false)}>Cancel</Button>
            <Button onClick={handleAddCurrency}>Add Currency</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Exchange Rate Dialog */}
      <Dialog open={showRateDialog} onOpenChange={setShowRateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Exchange Rate</DialogTitle>
            <DialogDescription>Add a new currency exchange rate</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>From Currency</Label>
                <Input
                  placeholder="USD"
                  value={newRate.from_currency}
                  onChange={(e) => setNewRate({ ...newRate, from_currency: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>To Currency</Label>
                <Input
                  placeholder="ETB"
                  value={newRate.to_currency}
                  onChange={(e) => setNewRate({ ...newRate, to_currency: e.target.value })}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Exchange Rate</Label>
                <Input
                  type="number"
                  step="0.0001"
                  placeholder="55.50"
                  value={newRate.rate}
                  onChange={(e) => setNewRate({ ...newRate, rate: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Effective Date</Label>
                <Input
                  type="date"
                  value={newRate.effective_date}
                  onChange={(e) => setNewRate({ ...newRate, effective_date: e.target.value })}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRateDialog(false)}>Cancel</Button>
            <Button onClick={handleAddRate}>Add Rate</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
